import React, { useState } from 'react';

interface NotificationSettingsProps {
  onSave: (settings: any) => Promise<void>;
  isSaving: boolean;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ onSave, isSaving }) => {
  const [formData, setFormData] = useState({
    emailNotifications: true,
    statementNotifications: true,
    paymentNotifications: true,
    expenseNotifications: true,
    reminderDays: 7,
    emailSender: 'no-reply@ledgiventa.com',
    emailFooter: 'Powered by Ledgiventa - Service Charge Management',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: target.checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-md">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Notification Settings</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure when and how notifications are sent.
        </p>
        
        <div className="mt-6 space-y-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="emailNotifications"
                name="emailNotifications"
                type="checkbox"
                checked={formData.emailNotifications}
                onChange={handleInputChange}
                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="emailNotifications" className="font-medium text-gray-700">
                Enable Email Notifications
              </label>
              <p className="text-gray-500">
                Send email notifications for important events.
              </p>
            </div>
          </div>
          
          {formData.emailNotifications && (
            <>
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="statementNotifications"
                    name="statementNotifications"
                    type="checkbox"
                    checked={formData.statementNotifications}
                    onChange={handleInputChange}
                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="statementNotifications" className="font-medium text-gray-700">
                    Statement Notifications
                  </label>
                  <p className="text-gray-500">
                    Send notifications when new statements are issued.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="paymentNotifications"
                    name="paymentNotifications"
                    type="checkbox"
                    checked={formData.paymentNotifications}
                    onChange={handleInputChange}
                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="paymentNotifications" className="font-medium text-gray-700">
                    Payment Notifications
                  </label>
                  <p className="text-gray-500">
                    Send notifications when payments are received.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="expenseNotifications"
                    name="expenseNotifications"
                    type="checkbox"
                    checked={formData.expenseNotifications}
                    onChange={handleInputChange}
                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="expenseNotifications" className="font-medium text-gray-700">
                    Expense Notifications
                  </label>
                  <p className="text-gray-500">
                    Send notifications when new expenses are added.
                  </p>
                </div>
              </div>
              
              <div className="sm:col-span-3">
                <label htmlFor="reminderDays" className="block text-sm font-medium text-gray-700">
                  Payment Reminder Days
                </label>
                <div className="mt-1">
                  <input
                    type="number"
                    name="reminderDays"
                    id="reminderDays"
                    min={1}
                    max={30}
                    value={formData.reminderDays}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Number of days before due date to send payment reminders.
                </p>
              </div>
              
              <div>
                <label htmlFor="emailSender" className="block text-sm font-medium text-gray-700">
                  Email Sender Address
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="emailSender"
                    id="emailSender"
                    value={formData.emailSender}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="emailFooter" className="block text-sm font-medium text-gray-700">
                  Email Footer Text
                </label>
                <div className="mt-1">
                  <textarea
                    name="emailFooter"
                    id="emailFooter"
                    rows={3}
                    value={formData.emailFooter}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default NotificationSettings;
